import * as yup from 'yup'
import { validateSpanishId } from './dniValidation'

/**
 * Validation Schemas for Olvidadme Forms
 * 
 * Using Yup for consistent validation across frontend and backend
 * Includes Spanish-specific validations (DNI/NIE, phone, postal codes)
 * GDPR compliance validation for legal platform
 */

// ==========================================
// CUSTOM VALIDATION METHODS
// ==========================================

/**
 * Spanish ID (DNI/NIE) validation method
 */
yup.addMethod(yup.string, 'spanishId', function(message = 'DNI/NIE inválido') {
  return this.test('spanish-id', message, function(value) {
    if (!value) return true // Let required() handle empty values
    
    const validation = validateSpanishId(value)
    
    if (!validation.isValid) {
      return this.createError({
        message: validation.error || message
      })
    }
    
    return true
  })
})

/**
 * Spanish phone number validation
 */
yup.addMethod(yup.string, 'spanishPhone', function(message = 'Teléfono español inválido') {
  return this.test('spanish-phone', message, function(value) {
    if (!value) return true // Let required() handle empty values
    
    // Clean phone number
    const clean = value.replace(/[\s\-\(\)]/g, '')
    
    // Spanish phone patterns:
    // Mobile: 6XX XXX XXX or 7XX XXX XXX (9 digits)
    // Landline: 9XX XXX XXX (9 digits, area codes 91, 93, 94, 95, 96, 98)
    // International: +34 followed by 9 digits
    
    const mobilePattern = /^[67]\d{8}$/
    const landlinePattern = /^9[1-9]\d{7}$/
    const internationalPattern = /^\+34[67]\d{8}$|^\+349[1-9]\d{7}$/
    
    const isValid = mobilePattern.test(clean) || 
                   landlinePattern.test(clean) || 
                   internationalPattern.test(clean)
    
    if (!isValid) {
      return this.createError({
        message: 'Introduzca un teléfono español válido (6XX XXX XXX, 9XX XXX XXX, o +34...)'
      })
    }
    
    return true
  })
})

/**
 * Spanish postal code validation
 */
yup.addMethod(yup.string, 'spanishPostalCode', function(message = 'Código postal español inválido') {
  return this.test('spanish-postal', message, function(value) {
    if (!value) return true // Let required() handle empty values
    
    // Spanish postal codes: 5 digits, first 2 digits indicate province (01-52)
    const postalPattern = /^(0[1-9]|[1-4]\d|5[0-2])\d{3}$/
    
    if (!postalPattern.test(value)) {
      return this.createError({
        message: 'Código postal debe ser de 5 dígitos (01000-52999)'
      })
    }
    
    return true
  })
})

/**
 * Password strength validation
 */
yup.addMethod(yup.string, 'passwordStrength', function(message = 'Contraseña debe cumplir los requisitos de seguridad') {
  return this.test('password-strength', message, function(value) {
    if (!value) return true // Let required() handle empty values
    
    const minLength = 8
    const hasUppercase = /[A-Z]/.test(value)
    const hasLowercase = /[a-z]/.test(value)
    const hasNumbers = /\d/.test(value)
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(value)
    
    if (value.length < minLength) {
      return this.createError({
        message: `Contraseña debe tener al menos ${minLength} caracteres`
      })
    }
    
    if (!hasUppercase) {
      return this.createError({
        message: 'Contraseña debe contener al menos una mayúscula'
      })
    }
    
    if (!hasLowercase) {
      return this.createError({
        message: 'Contraseña debe contener al menos una minúscula'
      })
    }
    
    if (!hasNumbers) {
      return this.createError({
        message: 'Contraseña debe contener al menos un número'
      })
    }
    
    if (!hasSpecialChar) {
      return this.createError({
        message: 'Contraseña debe contener al menos un carácter especial'
      })
    }
    
    return true
  })
})

// ==========================================
// SHARED FIELD SCHEMAS
// ==========================================

export const emailSchema = yup
  .string()
  .required('Email es requerido')
  .email('Formato de email inválido')
  .max(100, 'Email no puede exceder 100 caracteres')
  .lowercase()

export const passwordSchema = yup
  .string()
  .required('Contraseña es requerida')
  .passwordStrength()

export const nameSchema = yup
  .string()
  .required('Nombre es requerido')
  .min(2, 'Nombre debe tener al menos 2 caracteres')
  .max(50, 'Nombre no puede exceder 50 caracteres')
  .matches(/^[a-zA-ZáéíóúüñÁÉÍÓÚÜÑ\s'-]+$/, 'Nombre solo puede contener letras, espacios, guiones y apostrofes')

export const firstNameSchema = yup
  .string()
  .required('Nombre es requerido')
  .min(2, 'Nombre debe tener al menos 2 caracteres')
  .max(50, 'Nombre no puede exceder 50 caracteres')
  .matches(/^[a-zA-ZáéíóúüñÁÉÍÓÚÜÑ\s'-]+$/, 'Nombre solo puede contener letras, espacios, guiones y apostrofes')

export const lastNameSchema = yup
  .string()
  .required('Apellidos son requeridos')
  .min(2, 'Apellidos deben tener al menos 2 caracteres')
  .max(50, 'Apellidos no pueden exceder 50 caracteres')
  .matches(/^[a-zA-ZáéíóúüñÁÉÍÓÚÜÑ\s'-]+$/, 'Apellidos solo pueden contener letras, espacios, guiones y apostrofes')

export const phoneSchema = yup
  .string()
  .required('Teléfono es requerido')
  .spanishPhone()

export const dniSchema = yup
  .string()
  .required('DNI/NIE es requerido')
  .spanishId()

export const postalCodeSchema = yup
  .string()
  .required('Código postal es requerido')
  .spanishPostalCode()

// ==========================================
// ADDRESS SCHEMA
// ==========================================

export const addressSchema = yup.object({
  street: yup
    .string()
    .required('Dirección es requerida')
    .max(100, 'Dirección no puede exceder 100 caracteres'),
  
  city: yup
    .string()
    .required('Ciudad es requerida')
    .max(50, 'Ciudad no puede exceder 50 caracteres'),
  
  postalCode: postalCodeSchema,
  
  province: yup
    .string()
    .required('Provincia es requerida')
    .max(50, 'Provincia no puede exceder 50 caracteres'),
  
  autonomousCommunity: yup
    .string()
    .required('Comunidad autónoma es requerida')
    .oneOf([
      'andalucia', 'aragon', 'asturias', 'baleares', 'canarias', 'cantabria',
      'castilla_la_mancha', 'castilla_y_leon', 'cataluna', 'extremadura',
      'galicia', 'la_rioja', 'madrid', 'murcia', 'navarra', 'pais_vasco',
      'valencia', 'ceuta', 'melilla'
    ], 'Comunidad autónoma no válida'),
  
  hasForalRights: yup.boolean().default(false),
  
  foralTerritory: yup
    .string()
    .nullable()
    .when('hasForalRights', {
      is: true,
      then: schema => schema
        .required('Territorio foral es requerido')
        .oneOf([
          'euskadi', 'navarra', 'catalunya'
        ], 'Territorio foral no válido'),
      otherwise: schema => schema.nullable()
    })
})

// ==========================================
// GDPR CONSENT SCHEMA
// ==========================================

export const gdprConsentSchema = yup.object({
  termsAndConditions: yup
    .boolean()
    .required('Debe aceptar los términos y condiciones')
    .oneOf([true], 'Debe aceptar los términos y condiciones'),
  
  privacyPolicy: yup
    .boolean()
    .required('Debe aceptar la política de privacidad')
    .oneOf([true], 'Debe aceptar la política de privacidad'),
  
  dataProcessing: yup
    .boolean()
    .required('Debe aceptar el tratamiento de datos personales')
    .oneOf([true], 'Debe aceptar el tratamiento de datos personales'),
  
  marketing: yup
    .boolean()
    .default(false)
})

// ==========================================
// LOGIN FORM SCHEMA
// ==========================================

export const loginSchema = yup.object({
  email: yup
    .string()
    .required('Email es requerido')
    .email('Introduzca un email válido'),
  
  password: yup
    .string()
    .required('Contraseña es requerida'),
  
  rememberMe: yup
    .boolean()
    .default(false)
})

// ==========================================
// REGISTER FORM SCHEMA
// ==========================================

export const registerSchema = yup.object({
  // Personal information - separate firstName and lastName
  firstName: firstNameSchema,
  lastName: lastNameSchema,
  email: emailSchema,
  password: passwordSchema,
  confirmPassword: yup
    .string()
    .required('Confirme su contraseña')
    .oneOf([yup.ref('password'), null], 'Las contraseñas no coinciden'),
  
  // Spanish identification
  dni: dniSchema,
  phone: phoneSchema,
  
  // Address information
  address: addressSchema,
  
  // GDPR consents (plural) with backend field names
  gdprConsents: gdprConsentSchema,
  
  // Legal capacity (for representatives)
  legalCapacity: yup
    .string()
    .nullable()
    .oneOf([null, 'self', 'guardian', 'representative'], 'Capacidad legal no válida'),
  
  // Service preferences
  preferredLanguage: yup
    .string()
    .default('es')
    .oneOf(['es', 'ca', 'eu', 'gl'], 'Idioma no soportado')
})

// ==========================================
// FORGOT PASSWORD SCHEMA
// ==========================================

export const forgotPasswordSchema = yup.object({
  email: emailSchema
})

// ==========================================
// RESET PASSWORD SCHEMA
// ==========================================

export const resetPasswordSchema = yup.object({
  newPassword: passwordSchema,

  confirmPassword: yup
    .string()
    .required('Confirme su nueva contraseña')
    .oneOf([yup.ref('newPassword'), null], 'Las contraseñas no coinciden')
})

// ==========================================
// PROFILE UPDATE SCHEMA
// ==========================================

export const profileUpdateSchema = yup.object({
  name: nameSchema,
  phone: phoneSchema,
  
  // Address is optional for updates
  address: addressSchema.nullable(),
  
  // GDPR consent updates
  gdprConsent: yup.object({
    marketing: yup.boolean().default(false),
    analytics: yup.boolean().default(false),
    thirdParty: yup.boolean().default(false)
  }),
  
  // Preferences
  preferredLanguage: yup
    .string()
    .oneOf(['es', 'ca', 'eu', 'gl'], 'Idioma no soportado'),
  
  // Newsletter subscription
  subscribedToNewsletter: yup.boolean().default(false)
})

// ==========================================
// EXPORTS
// ==========================================

export default {
  loginSchema,
  registerSchema,
  forgotPasswordSchema,
  resetPasswordSchema,
  profileUpdateSchema,
  
  // Field schemas for reuse
  emailSchema,
  passwordSchema,
  nameSchema,
  firstNameSchema,
  lastNameSchema,
  phoneSchema,
  dniSchema,
  postalCodeSchema,
  addressSchema,
  gdprConsentSchema
}